import { Trans, useTranslation } from "react-i18next";
import { useState } from "react";
import { ChevronDown, ChevronUp, Flag } from "lucide-react";

import { ResizableSidebar } from "@/components/ResizableSidebar";
import { Spinner } from "@/components/ui/v2/spinner";
import { cn } from "@/utils/cn";
import type { ILeaveApprovalSteps } from "@/types/(my-space)/leaves.interface";
import type {
  IAssetApprovalSteps,
  IApprovalStep,
} from "@/types/(company)/assets.interface";
import type { IApprovalSteps } from "@/types/(company)/workflow.interface";
import ApprovalStepsFlow from "./approval-steps-flow";

// ── Types ─────────────────────────────────────────────────────────────────────

type StepDisplayStatus = "approved" | "current" | "pending" | "rejected";

export interface ApprovalFlowPanelProps {
  approvalSteps?: ILeaveApprovalSteps | IAssetApprovalSteps | IApprovalSteps;
  triggerContent?: React.ReactNode;
  isLoading?: boolean;
  /** Label shown in the header. Default: "Approval Flow" */
  title?: string;
  /** Show the progress bar + step dots above the flow canvas. Default: true */
  showProgress?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const getStepStatus = (step: IApprovalStep): StepDisplayStatus => {
  if (step.status === "approved") return "approved";
  if (step.status === "rejected") return "rejected";
  if (step.isCurrent && step.status === "pending") return "current";
  return "pending";
};

// ── ApprovalFlowPanel — embeddable, no sidebar wrapper ────────────────────────

export const ApprovalFlowPanel = ({
  approvalSteps,
  triggerContent,
  isLoading,
  title: titleProp,
  showProgress = true,
}: ApprovalFlowPanelProps) => {
  const { t } = useTranslation(["common", "my-space"]);
  const title = titleProp ?? t("my-space:labels.approval_flow");
  const steps = (approvalSteps?.steps ?? []) as IApprovalStep[];
  const instanceStatus = approvalSteps?.instanceStatus;
  const approvedCount = steps.filter((s) => s.status === "approved").length;
  const rejectedCount = steps.filter((s) => s.status === "rejected").length;
  const isRejected = instanceStatus === "rejected";

  return (
    <div className="flex flex-col bg-white h-full">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {approvalSteps && (
          <span className="text-xs text-muted-foreground tabular-nums">
            {/* <Trans> keeps the bold number inside the translated phrase. */}
            <Trans
              i18nKey="common:workflow.n_steps"
              count={approvalSteps.totalSteps}
              components={{
                1: <span className="font-semibold text-gray-700" />,
              }}
            />
          </span>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* ── Progress bar ─────────────────────────────────────── */}
        {showProgress && approvalSteps && steps.length > 0 && (
          <div className="px-4 pt-3 pb-4 border-b border-gray-100 bg-gray-50/60 shrink-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {t("common:workflow.progress")}
              </span>
              <span
                className={cn(
                  "text-[10px] font-bold",
                  isRejected ? "text-red-600" : "text-gray-600",
                )}
              >
                {isRejected
                  ? t("common:workflow.n_approved_m_rejected", {
                      approved: approvedCount,
                      rejected: rejectedCount,
                    })
                  : t("common:workflow.n_of_m_approved", {
                      approved: approvedCount,
                      total: steps.length,
                    })}
              </span>
            </div>
            <div className="flex gap-1 mt-2">
              {steps.map((step) => {
                const s = getStepStatus(step);
                return (
                  <div
                    key={step.stepOrder}
                    title={step.stepName}
                    className={cn(
                      "flex-1 h-1 rounded-full transition-colors",
                      s === "approved" && "bg-success",
                      s === "rejected" && "bg-red-500",
                      s === "current" && "bg-orange-500",
                      s === "pending" && "bg-border",
                    )}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* ── Flow canvas ──────────────────────────────────────── */}
        <div className="flex-1 min-h-[320px] sm:min-h-[400px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Spinner />
            </div>
          ) : !approvalSteps ? (
            <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-center">
              <Flag className="w-5 h-5 text-gray-300" />
              <p className="text-sm text-muted-foreground">
                {t("common:workflow.no_workflow_configured")}
              </p>
            </div>
          ) : (
            <ApprovalStepsFlow
              approvalSteps={approvalSteps}
              showHeader={false}
              triggerContent={triggerContent}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ── ApprovalFlowSidebar removed — use RequestDetailSidebar + ApprovalFlowPanel ──
// kept as named function to avoid breaking any default imports still in use
const ApprovalFlowSidebar = ({
  approvalSteps,
  triggerContent,
  isLoading,
  title: titleProp,
  showProgress = true,
  defaultWidth = 320,
  minWidth,
  maxWidth,
}: ApprovalFlowSidebarProps) => {
  const { t } = useTranslation(["common", "my-space"]);
  const title = titleProp ?? t("my-space:labels.approval_flow");
  const steps = (approvalSteps?.steps ?? []) as IApprovalStep[];
  const instanceStatus = approvalSteps?.instanceStatus;
  const approvedCount = steps.filter((s) => s.status === "approved").length;
  const rejectedCount = steps.filter((s) => s.status === "rejected").length;
  const isRejected = instanceStatus === "rejected";

  // Mobile-only expand/collapse (desktop uses ResizableSidebar's own collapse)
  const [mobileExpanded, setMobileExpanded] = useState(true);

  return (
    <ResizableSidebar
      title={title}
      defaultWidth={defaultWidth}
      minWidth={minWidth}
      maxWidth={maxWidth}
    >
      <div className="flex flex-col bg-white lg:h-full">
        {/* ── Header ───────────────────────────────────────────── */}
        <button
          type="button"
          onClick={() => setMobileExpanded((v) => !v)}
          className="flex items-center justify-between w-full px-4 py-3 text-start border-b border-gray-100 shrink-0 lg:cursor-default"
        >
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <div className="flex items-center gap-2">
            {approvalSteps && (
              <span className="text-xs text-muted-foreground tabular-nums">
                <Trans
                  i18nKey="common:workflow.n_steps"
                  count={approvalSteps.totalSteps}
                  components={{
                    1: <span className="font-semibold text-gray-700" />,
                  }}
                />
              </span>
            )}
            {/* Chevron visible only on mobile */}
            <span className="text-gray-400 lg:hidden">
              {mobileExpanded ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </span>
          </div>
        </button>

        {/* ── Body (collapsible on mobile, always visible on desktop) ── */}
        <div
          className={cn(
            "flex flex-col lg:flex-1 lg:min-h-0",
            !mobileExpanded && "hidden lg:flex",
          )}
        >
          {/* ── Progress bar ─────────────────────────────────────── */}
          {showProgress && approvalSteps && steps.length > 0 && (
            <div className="px-4 pt-3 pb-4 border-b border-gray-100 bg-gray-50/60 shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {t("common:workflow.progress")}
                </span>
                <span
                  className={cn(
                    "text-[10px] font-bold",
                    isRejected ? "text-red-600" : "text-gray-600",
                  )}
                >
                  {isRejected
                    ? `${approvedCount} approved · ${rejectedCount} rejected`
                    : `${approvedCount} of ${steps.length} approved`}
                </span>
              </div>
              <div className="flex gap-1 mt-2">
                {steps.map((step) => {
                  const s = getStepStatus(step);
                  return (
                    <div
                      key={step.stepOrder}
                      title={step.stepName}
                      className={cn(
                        "flex-1 h-1 rounded-full transition-colors",
                        s === "approved" && "bg-success",
                        s === "rejected" && "bg-red-500",
                        s === "current" && "bg-orange-500",
                        s === "pending" && "bg-border",
                      )}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Flow canvas ──────────────────────────────────────── */}
          <div className="h-[400px] lg:flex-1 lg:min-h-0 lg:h-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Spinner />
              </div>
            ) : !approvalSteps ? (
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-center">
                <Flag className="w-5 h-5 text-gray-300" />
                <p className="text-sm text-muted-foreground">
                  {t("common:workflow.no_workflow_configured")}
                </p>
              </div>
            ) : (
              <ApprovalStepsFlow
                approvalSteps={approvalSteps}
                showHeader={false}
                triggerContent={triggerContent}
              />
            )}
          </div>
        </div>
      </div>
    </ResizableSidebar>
  );
};

export default ApprovalFlowSidebar;
